/* eslint-disable @typescript-eslint/no-unused-vars */
import { FormControl, FormLabel, HStack, StackItem, useToast, Input, Select } from '@chakra-ui/react';
import { DateRangePreSelector, ExpertisePointSelect, LocalizedRangeDatepicker } from 'components';
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { ExpertisePointQueryCacheKey, ExpertisePointGroupType, ExpertisePointGroupTypeLabel } from 'types';
import { Api } from 'api';
import { useAuthUser } from 'core';
import { t, Trans } from '@lingui/macro';
import { AdminPageTitle } from '../components';
import { AdminTransactionQueriesTable } from '../components/AdminTransactionQueriesTable';

export const AdminTransactionsPage = () => {
  const toast = useToast();
  const [expertisePointId, setExpertisePointId] = useState<number>(0);
  const [selectedBrand, setSelectedBrand] = useState<number>(() => {
    const saved = localStorage.getItem('selectedExpertisePointGroupType');
    return saved ? parseInt(saved, 10) : 0;
  });
  const authUserInfo = useAuthUser();
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    new Date(),
  ]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handleExpertisePointGroupTypeChange = (event: any) => {
      const { value } = event.detail;
      setSelectedBrand(value ? parseInt(value, 10) : 0);
    };

    window.addEventListener('expertisePointGroupTypeChanged', handleExpertisePointGroupTypeChange);
    return () => window.removeEventListener('expertisePointGroupTypeChanged', handleExpertisePointGroupTypeChange);
  }, []);

  const { data, isLoading } = useQuery(
    [
      ExpertisePointQueryCacheKey.GetTransactionsSource,
      expertisePointId,
      selectedDates[0],
      selectedDates[1],
      selectedBrand,
    ],
    async () => {
      if (selectedDates.length === 2) {
        const response = await Api.ExpertisePoint.getTransactionsBySource(
          expertisePointId === 0 ? undefined : expertisePointId,
          selectedDates[0].toISOString(),
          selectedDates[1].toISOString(),
          selectedBrand === 0 ? undefined : selectedBrand,
        );
        return response.data;
      }
      return [];
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const filteredData = useMemo(() => {
    if (!data || !searchTerm) return data;
    return data.filter(
      (item) =>
        item.plateNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.chassisNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [data, searchTerm]);

  return (
    <>
      <AdminPageTitle title={t`İşlem Sorguları`} />

      <HStack align="center" gap={10}>
        <StackItem>
          <DateRangePreSelector
            onDateRangeSelected={(startDate: Date, endDate: Date) => {
              setSelectedDates([startDate, endDate]);
            }}
          />
        </StackItem>
        {authUserInfo?.isSuperAdmin && (
          <>
            <StackItem w="200px">
              <FormControl>
                <FormLabel>
                  <Trans>Marka</Trans>
                </FormLabel>
                <Select value={selectedBrand} onChange={(e) => setSelectedBrand(Number(e.target.value))}>
                  <option value={0}>Hepsi</option>
                  {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </StackItem>
            <StackItem w="300px">
              <ExpertisePointSelect
                enableAll
                groupType={selectedBrand === 0 ? undefined : selectedBrand}
                onExpertisePointSelected={(selectedExpertisePoint) => {
                  if (selectedExpertisePoint?.id) {
                    setExpertisePointId(selectedExpertisePoint?.id);
                  }
                }}
              />
            </StackItem>
          </>
        )}
      </HStack>

      <HStack align="center" spacing={4} marginBottom={55}>
        <FormControl width={['100%', '100%', '25%']} paddingTop={50}>
          <FormLabel>
            <Trans>Tarih Aralığı</Trans>
          </FormLabel>
          <LocalizedRangeDatepicker selectedDates={selectedDates} onDateChange={setSelectedDates} />
        </FormControl>
        <FormControl width={['100%', '100%', '25%']} paddingTop={50}>
          <FormLabel>
            <Trans>Plaka/Şasi No Ara</Trans>
          </FormLabel>
          <Input
            placeholder="Plaka veya Şasi No ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </FormControl>
      </HStack>

      {filteredData && <AdminTransactionQueriesTable inquiriesData={filteredData} />}
    </>
  );
};
